import React from 'react';

import { Routes, Route, useRoutes } from 'react-router-dom';

import EventDetailPage from './EventDetailPage';
import { BalloonEventsMap } from '../components/BalloonEventsMap';
import { useBalloonEventsOptions } from '../contexts/EmbedOptions';
import { QueryProvider, RouterChecker, type RouterType, FilterRouteHandler } from 'react-ui';
import BalloonEventsMapBox from '../components/BalloonEventsMapBox';
import { ClientEmbedAPIProvider } from '../contexts/ClientEmbedAPI';
import { EventDataProvider } from '../contexts/EventData';
import { SponsorsProvider } from '../contexts/Partners';
import { QueryListener } from '../components/QueryListener';
import { useIsMobile } from "react-ui";
import { MapBoundingBoxProvider } from '../contexts/MapBoundingBox';
import { InitialFilterApplier } from '../components/InitialFilterApplier';
import { CreatorModalProvider } from '../contexts/CreatorModal';
import { AssistantModalProvider } from '../contexts/AssistantModal';
import { UserScopeProvider } from '../contexts/UserScope';
import { UserScopeEventSync } from '../components/UserScopeEventSync';
import { PartnerScopeProvider } from '../contexts/PartnerScope';
import { PartnerScopeEventSync } from '../components/PartnerScopeEventSync';
import { MapAutoFitHandler } from '../components/MapAutoFitHandler';
import { CreatorModalHandler } from '../components/CreatorModalHandler';
import { AssistantPanelHandler } from '../components/AssistantPanelHandler';
import { DeepLinkHandler } from '../components/DeepLinkHandler';
// Import eagerly instead of lazy for webcomponent builds
import BalloonEventsUI from './BalloonEventsUI';
import BalloonEventsMobileUI from './BalloonEventsMobileUI';

export interface LayoutProps {
    /**
     * Router type preference:
     * - 'browser': Use BrowserRouter (for standalone apps)
     * - 'hash': Use HashRouter (for embeds, web components)
     * - 'none': Use parent's router (for inline React component)
     */
    routerType: RouterType;
    clientEmbedAPI: any;
    clientEmbedEventTrigger: any;
}

export function Layout({
    routerType,
    clientEmbedAPI,
    clientEmbedEventTrigger,
}: LayoutProps) {
    const { baseurl, rootQuery, mobileRootQuery, initialFilter } = useBalloonEventsOptions();
    const isMobile = useIsMobile();
    return (
        <RouterChecker routerType={routerType} basename={baseurl || '/'}>
            <QueryProvider rootQuery={isMobile && mobileRootQuery ? mobileRootQuery : rootQuery}>
                <UserScopeProvider>
                <PartnerScopeProvider>
                <CreatorModalProvider>
                <AssistantModalProvider>
                    <BalloonEventsMapBox>
                        <MapBoundingBoxProvider>
                            <SponsorsProvider>
                                <EventDataProvider>
                                    <CreatorModalHandler />
                                    <AssistantPanelHandler />
                                    <DeepLinkHandler />
                                    <UserScopeEventSync />
                                    <PartnerScopeEventSync />
                                    <MapAutoFitHandler />
                                    <ClientEmbedAPIProvider
                                        api={clientEmbedAPI}
                                        trigger={clientEmbedEventTrigger}
                                    >
                                        <QueryListener />
                                        {/* Apply initial filter from props (passed from site app URL) */}
                                        {initialFilter && <InitialFilterApplier filter={initialFilter} />}
                                        <BalloonEventsMap />
                                        {/* Routes provide params context for EventDetails component */}
                                        <Routes>
                                            <Route path="/details/:slug" element={
                                                isMobile ? <BalloonEventsMobileUI /> : <BalloonEventsUI />
                                            } />
                                            <Route path="/event/:slug" element={
                                                isMobile ? <BalloonEventsMobileUI /> : <BalloonEventsUI />
                                            } />
                                            <Route path="/tags/:slug" element={
                                                <>
                                                    <FilterRouteHandler type="tags" />
                                                    {isMobile ? <BalloonEventsMobileUI /> : <BalloonEventsUI />}
                                                </>
                                            } />
                                            <Route path="/categories/:slug" element={
                                                <>
                                                    <FilterRouteHandler type="categories" />
                                                    {isMobile ? <BalloonEventsMobileUI /> : <BalloonEventsUI />}
                                                </>
                                            } />
                                            <Route path="/places/:slug" element={
                                                <>
                                                    <FilterRouteHandler type="places" />
                                                    {isMobile ? <BalloonEventsMobileUI /> : <BalloonEventsUI />}
                                                </>
                                            } />
                                            {/* /location/:slug is an alias for /places/:slug — same region filter. */}
                                            <Route path="/location/:slug" element={
                                                <>
                                                    <FilterRouteHandler type="places" />
                                                    {isMobile ? <BalloonEventsMobileUI /> : <BalloonEventsUI />}
                                                </>
                                            } />
                                            <Route path="/sources/:slug" element={
                                                <>
                                                    <FilterRouteHandler type="sources" />
                                                    {isMobile ? <BalloonEventsMobileUI /> : <BalloonEventsUI />}
                                                </>
                                            } />
                                            <Route path="/dates/:preset" element={
                                                <>
                                                    <FilterRouteHandler type="dates" />
                                                    {isMobile ? <BalloonEventsMobileUI /> : <BalloonEventsUI />}
                                                </>
                                            } />
                                            <Route path="*" element={
                                                isMobile ? <BalloonEventsMobileUI /> : <BalloonEventsUI />
                                            } />
                                        </Routes>
                                    </ClientEmbedAPIProvider>
                                </EventDataProvider>
                            </SponsorsProvider>
                        </MapBoundingBoxProvider>
                    </BalloonEventsMapBox>
                </AssistantModalProvider>
                </CreatorModalProvider>
                </PartnerScopeProvider>
                </UserScopeProvider>
            </QueryProvider>
        </RouterChecker>
    );
}